import type { Bulletin } from '@shared/types/Bulletin';
import { PAIE_FIELDS_TO_ROUND } from '@shared/constants/ParametresPaie';
import { plusPertinent } from '@domain/paie/BulletinsUniques';

/**
 * Correction des doublons de bulletins (décision du dirigeant du 22/10/2026, D1 de la recette 1.1.6 :
 * « si les bulletins sont annulés, il faudrait les supprimer »).
 *
 * Règles de sécurité :
 *  - on ne supprime que les doublons EXACTS (même salarié, même période, mêmes montants) d'un bulletin conservé ;
 *  - un bulletin validé ou payé n'est JAMAIS supprimé, même identique : il est seulement signalé ;
 *  - un bulletin de contenu différent n'est jamais supprimé ici (on ne sait pas lequel est juste) : il est signalé,
 *    et « Calculer la paie » le remplace tant qu'il est au statut « généré » ;
 *  - rien n'est décidé sur un bulletin annulé dont le contenu diffère (historique légitime d'une correction).
 */

export interface GroupeDoublons {
  salarieId: string;
  periode: string;
  /** Le bulletin qui reste (le plus avancé, puis le plus récent). */
  conserve: Bulletin;
  /** Doublons exacts « générés » ou « annulés » : supprimés par la correction. */
  aSupprimer: Bulletin[];
  /** Doublons exacts mais validés ou payés : jamais supprimés, à traiter à la main. */
  figesIdentiques: Bulletin[];
  /** Même salarié et même période mais montants différents : jamais supprimés. */
  differents: Bulletin[];
}

export interface PlanCorrectionDoublons {
  groupes: GroupeDoublons[];
  nbASupprimer: number;
  nbFigesIdentiques: number;
  nbDifferents: number;
}

/** Montants de calcul d'un bulletin, pour reconnaître un doublon exact. */
function montant(b: Bulletin, champ: (typeof PAIE_FIELDS_TO_ROUND)[number]): number {
  const v = b[champ];
  return typeof v === 'number' ? v : 0;
}

/** Deux bulletins du même salarié et de la même période ont-ils exactement les mêmes montants ? */
export function memeContenuBulletin(a: Bulletin, b: Bulletin): boolean {
  if (a.salarie_id !== b.salarie_id || a.periode !== b.periode) return false;
  return PAIE_FIELDS_TO_ROUND.every((champ) => montant(a, champ) === montant(b, champ));
}

/**
 * Plan de correction : lecture seule, rien n'est modifié.
 * @param periode limite le plan à une période ('AAAA-MM') ; absent = toutes les périodes.
 */
export function planifierCorrectionDoublons(
  bulletins: readonly Bulletin[],
  periode?: string,
): PlanCorrectionDoublons {
  const parCle = new Map<string, Bulletin[]>();
  for (const b of bulletins) {
    if (periode && b.periode !== periode) continue;
    const k = `${b.salarie_id}|${b.periode}`;
    parCle.set(k, [...(parCle.get(k) ?? []), b]);
  }

  const groupes: GroupeDoublons[] = [];
  for (const membres of parCle.values()) {
    if (membres.length < 2) continue;
    const vivants = membres.filter((b) => b.statut !== 'annule');
    const [premier, ...autres] = vivants;
    if (!premier) continue;
    const conserve = autres.reduce(plusPertinent, premier);

    const aSupprimer: Bulletin[] = [];
    const figesIdentiques: Bulletin[] = [];
    const differents: Bulletin[] = [];
    for (const b of membres) {
      if (b.id === conserve.id) continue;
      if (!memeContenuBulletin(b, conserve)) {
        if (b.statut !== 'annule') differents.push(b);
        continue;
      }
      if (b.statut === 'valide' || b.statut === 'paye') figesIdentiques.push(b);
      else aSupprimer.push(b);
    }
    if (aSupprimer.length + figesIdentiques.length + differents.length === 0) continue;
    groupes.push({
      salarieId: conserve.salarie_id,
      periode: conserve.periode,
      conserve,
      aSupprimer,
      figesIdentiques,
      differents,
    });
  }

  return {
    groupes,
    nbASupprimer: groupes.reduce((n, g) => n + g.aSupprimer.length, 0),
    nbFigesIdentiques: groupes.reduce((n, g) => n + g.figesIdentiques.length, 0),
    nbDifferents: groupes.reduce((n, g) => n + g.differents.length, 0),
  };
}
